import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  CartList,
  CheckoutInfoBox,
  Button,
  ButtonType,
  LoadingSpinner,
  LoadingSpinnerContainer,
  ErrorBox,
} from '@amzn/burberry-ui-components';
import type { CartListItem } from '@amzn/burberry-ui-components';
import { awsRum } from 'src/rum';
import { AppDispatch } from 'src/store/store';
import { getStage } from 'src/utils/roadieHelper';
import { selectCart, removeFromCart, setCart } from 'src/store/cartSlice';
import {
  hydrateCatalog,
  selectCatalog,
  selectHydrateCatalogComplete,
  selectCatalogError,
} from 'src/store/catalogSlice';
import { selectCsrfToken, selectClientId, selectExpiryTime, selectAuthState } from 'src/store/authSlice';
import { getClientId } from 'src/store/authSlice';
import { Paths } from 'src/paths/Paths';
import { metricEmitter } from 'src/utils/metricEmitter';
import { handleAuth, reloadWindowOnStorageChange } from 'src/utils/utils';
import { getRedirectUrlForCurrentStage } from 'src/constants/lwa';
import { buildProductUrl, findCatalogItemUsingPurchaseItem, getPriceNumberFromString, UH_OH, GENERIC_ERROR_STRING } from '@amzn/d2c-application-website-common';
import './Cart.scss';

const Cart: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const cart = useSelector(selectCart);
  const catalog = useSelector(selectCatalog);
  const hydrateCatalogComplete = useSelector(selectHydrateCatalogComplete);
  const catalogError = useSelector(selectCatalogError);
  const csrfToken = useSelector(selectCsrfToken);
  const clientId = useSelector(selectClientId);
  const expiryTime = useSelector(selectExpiryTime);
  const authState = useSelector(selectAuthState);
  const isLoggedIn = !!expiryTime && Date.now() < expiryTime;

  // Reload page when LWA popup stores auth state
  useEffect(() => {
    return reloadWindowOnStorageChange(dispatch, authState);
  }, [dispatch, authState]);

  const [pageLoadTime] = useState(Date.now());

  const isNotEmpty = cart?.items && cart.items.length > 0;

  // Page view metric
  useEffect(() => {
    awsRum?.recordEvent('PageView', { page: 'Cart' });
  }, []);

  // Fetch client ID for LWA
  useEffect(() => {
    if (!clientId) {
      dispatch(getClientId({ stage: getStage() }));
    }
  }, [dispatch, clientId]);

  const [catalogRequested, setCatalogRequested] = useState(false);
  const [pageLoadRecorded, setPageLoadRecorded] = useState(false);

  // Hydrate catalog from cart items
  const cartAsins = cart?.items?.map((i: any) => i.asin).join(',') ?? '';

  useEffect(() => {
    if (isNotEmpty) {
      setCatalogRequested(true);
      const identifiers = cart.items.map((item: any) => ({ itemAsin: item.asin }));
      dispatch(hydrateCatalog({
        itemIdentifiers: identifiers,
        stage: getStage(),
        locale: 'en-US',
        csrfToken,
      }));
    }
  }, [dispatch, cartAsins, csrfToken]);

  // Page load metric — only fires after a fresh catalog load triggered by this page
  useEffect(() => {
    if (pageLoadRecorded) return;
    if (!isNotEmpty || (catalogRequested && hydrateCatalogComplete)) {
      const loadTime = Date.now() - pageLoadTime;
      awsRum?.recordEvent('PageLoad', { page: 'Cart', loadTimeMs: String(loadTime) });
      setPageLoadRecorded(true);
    }
  }, [hydrateCatalogComplete, isNotEmpty, pageLoadTime, pageLoadRecorded, catalogRequested]);

  // Build CartListItems from cart + catalog
  const getCartListItems = (): CartListItem[] => {
    if (!cart?.items || !catalog) return [];
    return cart.items.map((cartItem: any) => {
      const catalogItem = findCatalogItemUsingPurchaseItem(catalog, { asin: cartItem.asin });
      const img = catalogItem?.images?.[0];
      return {
        id: cartItem.asin,
        image: typeof img === 'string' ? img : (img as any)?.highRes?.url || (img as any)?.lowRes?.url || '',
        title: catalogItem?.name || cartItem.asin,
        price: catalogItem?.price || '',
        quantity: cartItem.quantity,
        maxQuantity: catalogItem?.availability?.maxOrderQuantity ?? 99,
        variations: catalogItem?.variations?.dimensions?.map((dim: any) => ({
          key: dim.name || '',
          value: dim.values?.[0] || '',
        })),
      };
    });
  };

  const getSubtotal = (): string => {
    if (!cart?.items || !catalog) return '0.00';
    const total = cart.items.reduce((sum: number, cartItem: any) => {
      const catalogItem = findCatalogItemUsingPurchaseItem(catalog, { asin: cartItem.asin });
      const price = getPriceNumberFromString(catalogItem?.price || '0');
      return sum + price * cartItem.quantity;
    }, 0);
    return total.toFixed(2);
  };

  const handleRemoveItem = (index: number) => {
    const item = cart?.items?.[index];
    if (!item) return;
    awsRum?.recordEvent('CartRemoveItem', { asin: item.asin, index: String(index) });
    dispatch(removeFromCart({ purchaseItem: item, quantity: item.quantity ?? 1, subtotal: 0 }));
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const item = cart?.items?.[index];
    if (!item || !cart) return;
    awsRum?.recordEvent('CartQuantityChange', { asin: item.asin, quantity: String(quantity) });
    const updatedItems = cart.items.map((it: any, i: number) =>
      i === index ? { ...it, quantity } : it,
    );
    const newSubtotal = updatedItems.reduce((sum: number, it: any) => {
      const catalogItem = findCatalogItemUsingPurchaseItem(catalog || [], { asin: it.asin });
      const price = getPriceNumberFromString(catalogItem?.price || '0');
      return sum + price * it.quantity;
    }, 0);
    dispatch(setCart({ cart: { items: updatedItems, quantity: updatedItems.reduce((s: number, it: any) => s + it.quantity, 0), subtotal: newSubtotal } }));
  };

  const handleItemClick = (index: number) => {
    const item = cart?.items?.[index];
    if (!item) return;
    awsRum?.recordEvent('CartItemClick', { asin: item.asin });
    navigate(buildProductUrl(item.asin));
  };

  const handleContinueShopping = () => {
    awsRum?.recordEvent('ButtonClick', { action: 'ContinueShopping' });
    navigate(Paths.home);
  };

  const handleCheckout = () => {
    awsRum?.recordEvent('ButtonClick', { action: 'Checkout' });
    if (isLoggedIn) {
      navigate(Paths.previewCheckout);
    } else {
      handleAuth(clientId || '', getRedirectUrlForCurrentStage());
    }
  };

  // Loading state
  if (!hydrateCatalogComplete && isNotEmpty) {
    return (
      <div className="cartPage cartPage--loading" data-testid="cartPage">
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </div>
    );
  }

  // Error state
  if (catalogError) {
    return (
      <div className="cartPage cartPage--error" data-testid="cartPage">
        <ErrorBox
          heading={UH_OH}
          message={GENERIC_ERROR_STRING}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  const cartListItems = getCartListItems();
  const subtotal = getSubtotal();

  return (
    <div className="cartPage" data-testid="cartPage">
      <div className="cartPage__list">
        <CartList
          items={cartListItems}
          onRemoveItem={handleRemoveItem}
          onQuantityChange={handleQuantityChange}
          onItemClick={handleItemClick}
          metricEmitter={metricEmitter}
        />
      </div>

      <div className="cartPage__sidebar">
        {cartListItems.length === 0 ? (
          <Button
            type={ButtonType.CONTINUE_SHOPPING}
            onClick={handleContinueShopping}
            metricEmitter={metricEmitter}
          />
        ) : isLoggedIn ? (
          <>
            <CheckoutInfoBox
              subtotalAmount={`$${subtotal}`}
              cardTitle=""
              cardDescription=""
            />
            <Button
              type={ButtonType.BUY_WITH_AMAZON}
              onClick={handleCheckout}
              metricEmitter={metricEmitter}
            />
          </>
        ) : (
          <CheckoutInfoBox
            subtotalAmount={`$${subtotal}`}
          >
            <Button
              type={ButtonType.LOGIN_WITH_AMAZON}
              onClick={handleCheckout}
              metricEmitter={metricEmitter}
            />
          </CheckoutInfoBox>
        )}
      </div>
    </div>
  );
};

export default Cart;
